import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Button, ListItem, ListItemText, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { useFetch } from "utils/api/useFetch";
import request from "utils/apiRequestWithErrorCode";
import { StorageNotificationTemplate } from "../typeConfig";
import ListItemSkeleton from "./ListItemSkeleton";
import DefaultTemplateSection from "./DefaultTemplateSection";
import TemplateListSection from "./TemplateListSection";
import { useStyles } from "./styles";
import { PAGE_SIZE } from "./constants";

const API_PATH = "/storage-notification-templates";

export default function NotificationTemplates() {
  const { t } = useTranslation("storage");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const history = useHistory();
  const [deletingId, setDeletingId] = useState<string>(null);
  const [togglingDefaultId, setTogglingDefaultId] = useState<string>(null);
  const {
    data: templates,
    isLoading,
    mutate,
  } = useFetch<StorageNotificationTemplate[]>(API_PATH);
  const isEmpty = !templates || templates.length === 0;
  const defaultTemplate = (templates || []).find(t => t.defaultTemplate);
  const nonDefaultTemplates = (templates || [])
    .filter(t => !t.defaultTemplate)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  const handleDelete = async (template: StorageNotificationTemplate) => {
    setDeletingId(template.id);
    try {
      await request("DELETE", `${API_PATH}/${template.id}`);
      mutate(prev => prev?.filter(t => t.id !== template.id), {
        revalidate: false,
      });
      enqueueSnackbar(t("notificationTemplates.list.deleted"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("notificationTemplates.list.deleteError"), {
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  };

  async function handleToggleDefault(id: string, isCurrentDefault: boolean) {
    setTogglingDefaultId(id);
    try {
      const method = isCurrentDefault ? "DELETE" : "PUT";
      await request(method, `${API_PATH}/${id}/default`);
      mutate(onDefaultMutator(id, isCurrentDefault), { revalidate: false });
    } catch (err) {
      enqueueSnackbar(t("notificationTemplates.list.defaultError"), {
        variant: "error",
      });
    } finally {
      setTogglingDefaultId(null);
    }
  }

  return (
    <>
      <ListItem className={classes.headerRow}>
        <ListItemText
          secondary={t("notificationTemplates.list.subtitle")}
          primaryTypographyProps={{ component: "div" }}
          secondaryTypographyProps={{ variant: "caption" }}
          primary={
            <div className={classes.titleRow}>
              <Typography variant="subtitle2">
                {t("notificationTemplates.list.title")}
              </Typography>
              {!isEmpty && (
                <span className={classes.countBadge}>{templates.length}</span>
              )}
            </div>
          }
        />
        <Button
          variant="outlined"
          size="small"
          color="primary"
          startIcon={<AddIcon />}
          data-testid="storage-notification-templates__add"
          onClick={() =>
            history.push("/integrations/notification-templates/create")
          }
        >
          {t("notificationTemplates.list.add")}
        </Button>
      </ListItem>

      {isLoading && (
        <div className={classes.listContainer}>
          <div className={classes.list}>
            <ListItemSkeleton items={PAGE_SIZE} />
          </div>
        </div>
      )}

      {!isLoading && !isEmpty && (
        <div className={classes.listContainer}>
          <DefaultTemplateSection
            template={defaultTemplate}
            togglingDefault={
              !!togglingDefaultId && togglingDefaultId === defaultTemplate?.id
            }
            settingNewDefault={
              !!togglingDefaultId && togglingDefaultId !== defaultTemplate?.id
            }
            showDivider={nonDefaultTemplates.length > 0}
            onDelete={handleDelete}
            onToggleDefault={handleToggleDefault}
          />
          <TemplateListSection
            templates={nonDefaultTemplates}
            deletingId={deletingId}
            togglingDefaultId={togglingDefaultId}
            onDelete={handleDelete}
            onToggleDefault={handleToggleDefault}
          />
        </div>
      )}

      {!isLoading && isEmpty && (
        <div className={classes.listContainer}>
          <div className={classes.empty}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t("notificationTemplates.list.empty")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                history.push("/integrations/notification-templates/create")
              }
            >
              {t("notificationTemplates.list.createFirst")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function onDefaultMutator(id: string, isCurrentDefault: boolean) {
  return (prev: StorageNotificationTemplate[]) => {
    return prev?.map(t => {
      if (isCurrentDefault) {
        return t.id === id ? { ...t, defaultTemplate: false } : t;
      }
      return { ...t, defaultTemplate: t.id === id };
    });
  };
}
